import React, { useState, useEffect } from 'react';
import ErrorState from './ErrorState';

function Dashboard({ spotifyData: initialSpotifyData }) {
  const [activeTab, setActiveTab] = useState('weekly');
  const [spotifyData, setSpotifyData] = useState(initialSpotifyData);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'dark';
  });
  const { profile, top_artists, top_tracks, recently_played } = spotifyData || {};

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Check for data availability
  useEffect(() => {
    if (!spotifyData) {
      setError('Failed to load Spotify data');
    } else {
      setError(null);
    }
  }, [spotifyData]);

  // Set up auto-refresh interval
  useEffect(() => {
    const fetchUpdatedData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user-data`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSpotifyData(data);
        }
      } catch (error) {
        console.error('Error fetching updated data:', error);
      }
    };

    const intervalId = setInterval(fetchUpdatedData, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const handleRetry = async () => {
    try {
      const response = await fetch('/api/spotify-data');
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setSpotifyData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  if (error || !spotifyData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  const handleLogout = () => {
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    window.location.href = '/';
  };

  const handlePlay = (uri) => {
    window.open(uri, '_blank');
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  // Prepare data for different time ranges
  const timeRanges = {
    weekly: {
      artists: top_artists.short_term.items.slice(0, 5),
      tracks: top_tracks.short_term.items.slice(0, 5)
    },
    monthly: {
      artists: top_artists.medium_term.items.slice(0, 5),
      tracks: top_tracks.medium_term.items.slice(0, 5)
    },
    allTime: {
      artists: top_artists.long_term.items.slice(0, 10),
      tracks: top_tracks.long_term.items.slice(0, 10)
    },
    discover: {
      artists: top_artists.short_term.items.slice(0, 10),
      tracks: top_tracks.short_term.items.slice(0, 10)
    }
  };

  const currentData = timeRanges[activeTab];

  return (
    <div className="dashboard">
      <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
        {theme === 'dark' ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <button className="mobile-menu-dots" onClick={toggleModal} aria-label="Menu">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="6" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="18" r="2" />
        </svg>
      </button>

      <div className="dashboard-header">
        <div className="dashboard-title">
          <h1>Your Spotify Wrapped</h1>
          <p>Real-time insights into your music</p>
        </div>

        <div className="header-right">
          <div className="profile-mini">
            {profile.images && profile.images.length > 0 ? (
              <img src={profile.images[0].url} alt={profile.display_name} className="profile-image-mini" />
            ) : (
              <div className="profile-image-mini">
                {profile.display_name ? profile.display_name[0].toUpperCase() : 'U'}
              </div>
            )}
            <div className="profile-info-mini">
              <p>{profile.display_name}</p>
              <span>{profile.followers.total} followers</span>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Menu Modal */}
      <div className={`modal-overlay ${showModal ? 'show' : ''}`} onClick={toggleModal}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-profile">
            <div className="profile-mini">
              {profile.images && profile.images.length > 0 ? (
                <img src={profile.images[0].url} alt={profile.display_name} className="profile-image-mini" />
              ) : (
                <div className="profile-image-mini">
                  {profile.display_name ? profile.display_name[0].toUpperCase() : 'U'}
                </div>
              )}
              <div className="profile-info-mini">
                <p>{profile.display_name}</p>
                <span>{profile.followers.total} followers</span>
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <button className="modal-logout" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Weekly
        </button>
        <button 
          className={`tab ${activeTab === 'monthly' ? 'active' : ''}`}
          onClick={() => setActiveTab('monthly')}
        >
          Monthly
        </button>
        <button 
          className={`tab ${activeTab === 'allTime' ? 'active' : ''}`}
          onClick={() => setActiveTab('allTime')}
        >
          All Time
        </button>
        <button 
          className={`tab ${activeTab === 'discover' ? 'active' : ''}`}
          onClick={() => setActiveTab('discover')}
        >
          Discover
        </button>
        <button 
          className={`tab ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => setActiveTab('recent')}
        >
          Recently Played
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'recent' ? (
          <div className="discover-section">
            <div className="discover-header">
              <h2>Recently Played</h2>
              <p>Your latest listening history</p>
            </div>
            
            <div className="tracks-list">
              {recently_played?.items?.slice(0, 20).map((item, index) => (
                <div 
                  key={`${item.track.id}-${item.played_at}`} 
                  className="discover-track-card"
                  style={{
                    animation: `slideIn 0.5s ease-out ${index * 0.05}s both`,
                    opacity: 0,
                    animationFillMode: 'forwards'
                  }}
                >
                  <div className="track-image-container">
                    <img src={item.track.album.images[0].url} alt={item.track.name} className="track-image-large" />
                    <div className="track-overlay">
                      <span className="track-duration">
                        {Math.floor(item.track.duration_ms / 60000)}:{String(Math.floor((item.track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                      </span>
                    </div>
                    <button 
                      className="play-button-overlay"
                      onClick={() => handlePlay(item.track.external_urls.spotify)}
                      aria-label="Play track"
                    >
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.8906 12.846C19.2196 12.5916 19.2196 12.0994 18.8906 11.845L8.30736 4.25025C7.92424 3.95751 7.39981 4.22099 7.39981 4.69715L7.39981 20.0039C7.39981 20.48 7.92424 20.7435 8.30736 20.4508L18.8906 12.846Z" fill="currentColor"/>
                      </svg>
                    </button>
                  </div>
                  <div className="track-info">
                    <h4>{item.track.name}</h4>
                    <p className="artist-name">{item.track.artists.map(artist => artist.name).join(', ')}</p>
                    <p className="album-name">{item.track.album.name}</p>
                    <div className="track-stats">
                      <span className="popularity">Popularity: {item.track.popularity}%</span>
                      <span className="played-at">Played: {new Date(item.played_at).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : activeTab !== 'discover' ? (
          <div className="discover-section">
            <div className="discover-header">
              <h2>{activeTab === 'weekly' ? 'Weekly Top Picks' : activeTab === 'monthly' ? 'Monthly Favorites' : 'All Time Best'}</h2>
              <p>Your most listened to music</p>
            </div>
            
            <div className="discover-grid">
              <div className="discover-artists">
                <h3>Top Artists</h3>
                <div className="artists-list">
                  {currentData.artists.map((artist) => (
                    <div key={artist.id} className="discover-artist-card">
                      <div className="artist-image-container">
                        <img src={artist.images[1].url} alt={artist.name} className="artist-image-large" />
                        <div className="artist-overlay">
                          <span className="popularity-score">Popularity: {artist.popularity}%</span>
                        </div>
                      </div>
                      <div className="artist-info">
                        <h4>{artist.name}</h4>
                        <div className="artist-stats">
                          <span>{(artist.followers.total / 1000000).toFixed(1)}M followers</span>
                          <span>•</span>
                          <span>{artist.genres.length} genres</span>
                        </div>
                        <div className="genre-tags">
                          {artist.genres.slice(0, 2).map((genre, i) => (
                            <span key={i} className="genre-tag">{genre}</span>
                          ))}
                        </div>
                        <button 
                          className="follow-button"
                          onClick={() => window.open(artist.external_urls.spotify, '_blank')}
                        >
                          Follow on Spotify
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="discover-tracks">
                <h3>Top Tracks</h3>
                <div className="tracks-list">
                  {currentData.tracks.map((track) => (
                    <div key={track.id} className="discover-track-card">
                      <div className="track-image-container">
                        <img src={track.album.images[1].url} alt={track.name} className="track-image-large" />
                        <div className="track-overlay">
                          <span className="track-duration">
                            {Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                          </span>
                        </div>
                        <button 
                          className="play-button-overlay"
                          onClick={() => handlePlay(track.external_urls.spotify)}
                          aria-label="Play track"
                        >
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.8906 12.846C19.2196 12.5916 19.2196 12.0994 18.8906 11.845L8.30736 4.25025C7.92424 3.95751 7.39981 4.22099 7.39981 4.69715L7.39981 20.0039C7.39981 20.48 7.92424 20.7435 8.30736 20.4508L18.8906 12.846Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>
                      <div className="track-info">
                        <h4>{track.name}</h4>
                        <p className="artist-name">{track.artists[0].name}</p>
                        <p className="album-name">{track.album.name}</p>
                        <div className="track-stats">
                          <span className="popularity">Popularity: {track.popularity}%</span>
                          <span className="release-date">Released: {new Date(track.album.release_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="discover-section">
            <div className="discover-header">
              <h2>Discover New Music</h2>
              <p>Based on your recent listening</p>
            </div>
            
            <div className="discover-grid">
              <div className="discover-artists">
                <h3>Artists You Might Like</h3>
                <div className="artists-list">
                  {currentData.artists.map((artist) => (
                    <div key={artist.id} className="discover-artist-card">
                      <div className="artist-image-container">
                        <img src={artist.images[1].url} alt={artist.name} className="artist-image-large" />
                        <div className="artist-overlay">
                          <span className="popularity-score">Popularity: {artist.popularity}%</span>
                        </div>
                      </div>
                      <div className="artist-info">
                        <h4>{artist.name}</h4>
                        <div className="artist-stats">
                          <span>{(artist.followers.total / 1000000).toFixed(1)}M followers</span>
                          <span>•</span>
                          <span>{artist.genres.length} genres</span>
                        </div>
                        <div className="genre-tags">
                          {artist.genres.slice(0, 2).map((genre, i) => (
                            <span key={i} className="genre-tag">{genre}</span>
                          ))}
                        </div>
                        <button 
                          className="follow-button"
                          onClick={() => window.open(artist.external_urls.spotify, '_blank')}
                        >
                          Follow on Spotify
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="discover-tracks">
                <h3>Recommended Tracks</h3>
                <div className="tracks-list">
                  {currentData.tracks.map((track) => (
                    <div key={track.id} className="discover-track-card">
                      <div className="track-image-container">
                        <img src={track.album.images[1].url} alt={track.name} className="track-image-large" />
                        <div className="track-overlay">
                          <span className="track-duration">
                            {Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                          </span>
                        </div>
                        <button 
                          className="play-button-overlay"
                          onClick={() => handlePlay(track.external_urls.spotify)}
                          aria-label="Play track"
                        >
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.8906 12.846C19.2196 12.5916 19.2196 12.0994 18.8906 11.845L8.30736 4.25025C7.92424 3.95751 7.39981 4.22099 7.39981 4.69715L7.39981 20.0039C7.39981 20.48 7.92424 20.7435 8.30736 20.4508L18.8906 12.846Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>
                      <div className="track-info">
                        <h4>{track.name}</h4>
                        <p className="artist-name">{track.artists[0].name}</p>
                        <p className="album-name">{track.album.name}</p>
                        <div className="track-stats">
                          <span className="popularity">Popularity: {track.popularity}%</span>
                          <span className="release-date">Released: {new Date(track.album.release_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <footer className="dashboard-footer">
        <p>Made with ♥️ by <a href="https://github.com/erxcc" target="_blank" rel="noopener noreferrer">Eric</a></p>
      </footer>
    </div>
  );
}

export default Dashboard; 